import { useState, useCallback, useRef, useEffect } from 'react'
import { CustomCellEditorProps, useGridCellEditor } from 'ag-grid-react'
import { Box, CircularProgress, styled } from '@mui/material'
import IconifyPicker from '@zunicornshift/mui-iconify-picker'
import { convertIconifyToSvgBlob } from '../utils/iconifyToSvg'
import TenantStorage from '../../../../utils/storage'
import { TicketTypeIcon } from './TicketTypeIcon'

const RootBox = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
})

const IconDisplay = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  margin: 0,
})

const PickerWrapper = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  opacity: 0,
  pointerEvents: 'all',
  '& .MuiIconifyPicker-root': {
    position: 'relative',
    zIndex: 1,
  },
})

const LoadingOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 100,
})

type IconCellEditorProps = CustomCellEditorProps & {
  value: string
}

export const IconCellEditor = (props: IconCellEditorProps) => {
  const [value, setValue] = useState<string>(props.value || '')
  const [uploading, setUploading] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  // マウント時にIconifyPickerの?ボタンを自動クリック
  useEffect(() => {
    if (pickerRef.current) {
      // IconifyPickerの?ボタンを探してクリック
      const button = pickerRef.current.querySelector('button')
      if (button) {
        setTimeout(() => {
          button.click()
        }, 50)
      }
    }
  }, [])

  useGridCellEditor({
    isCancelAfterEnd() {
      return false
    },
  })

  const handleIconSelect = useCallback(
    async (icon: string | null) => {
      if (!icon || !props.node || !props.api) return

      try {
        setUploading(true)

        // IconifyアイコンをSVG Blobに変換
        const svgBlob = await convertIconifyToSvgBlob(icon)

        // Fileオブジェクトを作成（拡張子を.svgにする）
        const svgFile = new File([svgBlob], `${icon.replace(/:/g, '-')}.svg`, {
          type: 'image/svg+xml',
        })

        // S3にアップロード
        const response = await TenantStorage.uploadFile(svgFile)
        const uploadedUrl = response.json.downloadUrl

        // URLが正しく取得できているか確認
        if (!uploadedUrl) {
          throw new Error('Upload succeeded but URL is empty')
        }

        // 値を更新
        props.onValueChange(uploadedUrl)
        setValue(uploadedUrl)

        // 少し待ってから編集を終了（値を反映させる）
        setTimeout(() => {
          if (props.api) {
            props.api.stopEditing(false)
          }
        }, 100)
      } catch (error) {
        console.error('Icon upload failed:', error)
        // エラー時は編集をキャンセル
        setTimeout(() => {
          if (props.api) {
            props.api.stopEditing(true)
          }
        }, 100)
      } finally {
        setUploading(false)
      }
    },
    [props.node, props.api]
  )

  return (
    <RootBox>
      {uploading && (
        <LoadingOverlay>
          <CircularProgress size={20} />
        </LoadingOverlay>
      )}
      <IconDisplay>
        <TicketTypeIcon value={value} />
      </IconDisplay>
      <PickerWrapper ref={pickerRef}>
        <IconifyPicker
          onChange={handleIconSelect}
          inputProps={{ sx: { visibility: 'hidden' } }}
        />
      </PickerWrapper>
    </RootBox>
  )
}
